import { Plus, Settings, Ban } from 'lucide-react';
import type { FAQ } from '../../services/api';

interface FAQTabProps {
  faqs: FAQ[];
  onAdd: () => void;
  onEdit: (faq: FAQ) => void;
  onDelete: (id: string) => void;
}

export default function FAQTab({ faqs, onAdd, onEdit, onDelete }: FAQTabProps) {
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-light-text">FAQ 管理</h2>
          <p className="text-sm text-light-muted">管理常见问题解答</p>
        </div>
        <button
          onClick={onAdd}
          className="bg-accent-blue text-white font-medium px-4 py-2 rounded-xl hover:shadow-lg hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          添加 FAQ
        </button>
      </div>

      {/* FAQ List */}
      <div className="bg-white rounded-2xl shadow-lg border border-light-border overflow-hidden">
        <div className="space-y-2 p-4">
          {faqs.map((faq) => (
            <div
              key={faq.id}
              className="border border-light-border rounded-xl p-4 hover:bg-light-hover transition-colors"
            >
              {/* Desktop Layout */}
              <div className="hidden sm:flex items-start justify-between gap-4">
                <div className="flex-1">
                  <p className="font-medium text-light-text mb-1">{faq.question}</p>
                  <p className="text-sm text-light-muted">{faq.answer}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className={`px-2 py-1 rounded-lg text-xs ${
                    faq.isActive
                      ? 'bg-green-100 text-green-600'
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    {faq.isActive ? '启用' : '禁用'}
                  </span>
                  <button
                    onClick={() => onEdit(faq)}
                    className="p-2 text-accent-blue hover:bg-blue-50 rounded-lg transition-colors"
                    title="编辑"
                  >
                    <Settings className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => onDelete(faq.id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="删除"
                  >
                    <Ban className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Mobile Layout */}
              <div className="sm:hidden">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <p className="font-medium text-light-text flex-1">{faq.question}</p>
                  <span className={`px-2 py-1 rounded-lg text-xs shrink-0 ${
                    faq.isActive
                      ? 'bg-green-100 text-green-600'
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    {faq.isActive ? '启用' : '禁用'}
                  </span>
                </div>
                <p className="text-sm text-light-muted mb-3">{faq.answer}</p>
                <div className="flex gap-2">
                  <button
                    onClick={() => onEdit(faq)}
                    className="flex items-center gap-1 px-3 py-2 text-sm text-accent-blue bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                  >
                    <Settings className="w-4 h-4" />
                    编辑
                  </button>
                  <button
                    onClick={() => onDelete(faq.id)}
                    className="flex items-center gap-1 px-3 py-2 text-sm text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors"
                  >
                    <Ban className="w-4 h-4" />
                    删除
                  </button>
                </div>
              </div>
            </div>
          ))}
          {faqs.length === 0 && (
            <div className="text-center py-8 text-light-muted">
              暂无 FAQ，点击「添加 FAQ」创建
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
